import axios from 'axios';
import { Util } from 'src/Util';
import {
  AdminPlan,
  ApiResult,
  BankTransferRequest,
  BankTransferStatus,
  BillingCatalog,
  BillingDashboard,
  BillingSettings,
  CreditStatus,
  OfferInput,
  PlanOffer,
  SubscriptionCredit,
  Paged,
  PlanFeatureDefinition,
  PlanInput
} from './types';

const url = (path: string) => Util.apiAuthUrl(`admin/billing/${path}`);

const FAILED: ApiResult<never> = {
  success: false,
  error: 'Could not reach the server. Check your connection and try again.'
};

/**
 * The shared axios interceptor (src/Util.ts) resolves calls to the response body, or to
 * `{ success: false, error }`. Anything else (network failure, stacked interceptors after a
 * hot reload) is turned into a failed result so pages never read from `undefined`.
 */
async function call<T>(request: Promise<unknown>): Promise<ApiResult<T>> {
  try {
    const body = await request;
    if (body && typeof body === 'object' && typeof (body as ApiResult<T>).success === 'boolean') {
      return body as ApiResult<T>;
    }
    return FAILED;
  } catch (error) {
    console.error('Billing request failed', error);
    return FAILED;
  }
}

export const billingErrorMessage = (res: ApiResult<unknown>, fallback: string) =>
  res.error || res.message || fallback;

export const BillingApi = {
  dashboard: () => call<BillingDashboard>(axios.get(url('dashboard'))),
  features: () => call<PlanFeatureDefinition[]>(axios.get(url('features'))),
  catalog: () => call<BillingCatalog>(axios.get(url('catalog'))),

  offers: () => call<PlanOffer[]>(axios.get(url('offers'))),
  createOffer: (input: OfferInput) => call<PlanOffer>(axios.post(url('offers'), input)),
  updateOffer: (id: string, changes: Partial<OfferInput>) =>
    call<PlanOffer>(axios.post(url(`offers/${id}`), changes)),
  deleteOffer: (id: string) => call<{ id: string }>(axios.delete(url(`offers/${id}`))),

  credits: (params: { status?: CreditStatus | 'OPEN' | ''; search?: string; page: number; limit: number }) =>
    call<Paged<SubscriptionCredit>>(
      axios.get(url('credits'), {
        params: {
          status: params.status || undefined,
          search: params.search?.trim() || undefined,
          page: params.page,
          limit: params.limit
        }
      })
    ),
  openCreditCount: () => call<{ count: number }>(axios.get(url('credits/open-count'))),
  markCreditPaid: (id: string, note: string) =>
    call<SubscriptionCredit>(axios.post(url(`credits/${id}/mark-paid`), { note })),
  extendCredit: (id: string, dueDate: string) =>
    call<SubscriptionCredit>(axios.post(url(`credits/${id}/extend`), { dueDate })),
  cancelCredit: (id: string, reason: string) =>
    call<SubscriptionCredit>(axios.post(url(`credits/${id}/cancel`), { reason })),

  plans: () => call<AdminPlan[]>(axios.get(url('plans'))),
  createPlan: (input: PlanInput) => call<AdminPlan>(axios.post(url('plans'), input)),
  updatePlan: (id: string, changes: Partial<PlanInput>) =>
    call<AdminPlan>(axios.post(url(`plans/${id}`), changes)),
  deletePlan: (id: string) => call<{ id: string }>(axios.delete(url(`plans/${id}`))),

  settings: () => call<BillingSettings>(axios.get(url('settings'))),
  saveSettings: (settings: BillingSettings) =>
    call<BillingSettings>(axios.post(url('settings'), settings)),

  bankTransfers: (params: {
    status?: BankTransferStatus | '';
    search?: string;
    page: number;
    limit: number;
  }) =>
    call<Paged<BankTransferRequest>>(
      axios.get(url('bank-transfers'), {
        params: {
          status: params.status || undefined,
          search: params.search?.trim() || undefined,
          page: params.page,
          limit: params.limit
        }
      })
    ),
  pendingBankTransferCount: () =>
    call<{ count: number }>(axios.get(url('bank-transfers/pending-count'))),
  approveBankTransfer: (id: string, note: string) =>
    call<BankTransferRequest>(axios.post(url(`bank-transfers/${id}/approve`), { note })),
  rejectBankTransfer: (id: string, reason: string, note: string) =>
    call<BankTransferRequest>(
      axios.post(url(`bank-transfers/${id}/reject`), { reason, note })
    ),

  /** Slips are private; they are fetched with the admin token and shown from a blob URL. */
  async slipObjectUrl(id: string): Promise<string | null> {
    try {
      const res = await axios.get(url(`bank-transfers/${id}/slip`), {
        responseType: 'blob'
      });
      const blob = res as unknown as Blob;
      return blob instanceof Blob && blob.size > 0 ? URL.createObjectURL(blob) : null;
    } catch (error) {
      console.error('Failed to load bank slip', error);
      return null;
    }
  }
};
